import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import type { StageChunkingReport } from "../../api/types";
import { formatDuration } from "../../utils/format";

type Partition = StageChunkingReport["chunking"];
type Timeline = NonNullable<Partition["timeline"]>;

const stageName = (stage: StageChunkingReport["stage"]) =>
  stage.startsWith("lift-to-") ? stage.slice(8).toUpperCase() : stage;

/** Partial reruns can carry different partitions, even with identical totals. */
export const hasSharedPartition = (entries: StageChunkingReport[]) => {
  const first = entries[0]?.chunking;
  const timeline = entries.find(entry => entry.chunking.timeline)?.chunking.timeline;
  return entries.every(
    ({ chunking }) =>
      first &&
      chunking.windows === first.windows &&
      chunking.idleGapCuts === first.idleGapCuts &&
      chunking.longestPauseCuts === first.longestPauseCuts &&
      chunking.maxSpanCuts === first.maxSpanCuts &&
      (!timeline ||
        !chunking.timeline ||
        JSON.stringify(timeline) === JSON.stringify(chunking.timeline)),
  );
};

// Match the idle treatment in the analytics timeline instead of filling long
// gaps with the text-muted color. Use the same swatches in the legend and track.
const activityBackground = "var(--color-chart-blue)";
const idleBackground =
  "repeating-linear-gradient(45deg, var(--color-surface-strong) 0 3px, var(--color-outline-strong) 3px 5px)";
const cutLabel = {
  "recording-start": "recording_start",
  "idle-gap": "idle_gap_cut",
  "longest-pause": "longest_pause_cut",
  "max-span": "span_limit_cut",
} as const;

const Stats = ({ items }: { items: { label: string; value: number | string }[] }) => (
  <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
    {items.map(item => (
      <div
        key={item.label}
        className="rounded-xl border border-outline-strong bg-surface px-3 py-2.5"
      >
        <dt className="text-[11px] font-medium uppercase tracking-wider text-text-dim">
          {item.label}
        </dt>
        <dd className="mt-1 text-xl font-semibold tabular-nums text-text">{item.value}</dd>
      </div>
    ))}
  </dl>
);

const PartitionStats = ({ partition }: { partition: Partition }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    <Stats
      items={[
        { label: t("total_virtual_recordings"), value: partition.windows },
        { label: t("idle_gap_cuts"), value: partition.idleGapCuts },
        { label: t("longest_pause_cuts"), value: partition.longestPauseCuts },
        { label: t("max_span_cuts"), value: partition.maxSpanCuts },
      ]}
    />
  );
};

const fullDate = (timestamp: number) =>
  new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

/** Two aligned tracks share the full wall-clock axis; idle gaps are never compressed. */
export const VirtualRecordingTimeline = ({ timeline }: { timeline: Timeline }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  const [selected, setSelected] = useState(0);
  const selectId = useId();
  const start = Date.parse(timeline.start);
  const end = Date.parse(timeline.end);
  const duration = end - start;
  if (!(duration > 0) || timeline.windows.length === 0) {
    return <p className="text-sm text-text-mid">{t("timeline_no_duration")}</p>;
  }
  const position = (time: number) =>
    (100 * (Math.max(start, Math.min(end, time)) - start)) / duration;
  const selectedIndex = Math.min(selected, timeline.windows.length - 1);
  const window = timeline.windows[selectedIndex];
  const describe = (index: number) => {
    const item = timeline.windows[index];
    return (
      t("virtual_recording_number", { number: index + 1 }) +
      ": " +
      fullDate(Date.parse(item.start)) +
      " – " +
      fullDate(Date.parse(item.end)) +
      " · " +
      t(cutLabel[item.cut])
    );
  };
  return (
    <figure className="rounded-xl border border-outline-strong bg-surface p-4 dark:[color-scheme:dark]">
      <figcaption className="mb-2 text-sm font-semibold text-text">
        {t("recording_timeline")}
      </figcaption>
      <div className="mb-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-text-mid">
        {[
          { label: t("timeline_active"), background: activityBackground },
          { label: t("timeline_inactive"), background: idleBackground },
        ].map(item => (
          <span key={item.label} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-3 w-4 rounded-sm"
              style={{ background: item.background }}
            />
            {item.label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto pb-2">
        <div className="min-w-[640px] px-2 pt-2">
          <div className="relative h-[96px]" role="group" aria-label={t("recording_timeline")}>
            <div
              className="absolute inset-x-0 top-0 h-7 overflow-hidden rounded"
              style={{ background: activityBackground }}
              aria-label={t("timeline_activity_track")}
            >
              {timeline.idleSegments.map(([from, to], index) => (
                <span
                  key={index}
                  className="absolute inset-y-0"
                  title={
                    t("timeline_inactive") +
                    ": " +
                    fullDate(from * 1000) +
                    " – " +
                    fullDate(to * 1000)
                  }
                  style={{
                    background: idleBackground,
                    left: position(from * 1000) + "%",
                    width: Math.max(0, position(to * 1000) - position(from * 1000)) + "%",
                  }}
                />
              ))}
            </div>
            {timeline.windows.map((item, index) => {
              const left = position(Date.parse(item.start));
              const width = position(Date.parse(item.end)) - left;
              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <span
                      aria-hidden="true"
                      title={describe(index)}
                      className="absolute top-[-4px] z-10 h-10 w-2 -translate-x-1/2"
                      style={{ left: left + "%" }}
                    >
                      <span className="pointer-events-none absolute left-1/2 h-[67px] border-l-2 border-dashed border-muted" />
                    </span>
                  )}
                  <button
                    type="button"
                    aria-label={describe(index)}
                    aria-pressed={selectedIndex === index}
                    title={describe(index)}
                    onClick={() => setSelected(index)}
                    className="absolute top-10 h-6 overflow-hidden rounded border text-[10px] font-semibold text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
                    style={{
                      left: left + "%",
                      width: Math.max(0, width) + "%",
                      borderColor:
                        selectedIndex === index
                          ? "var(--color-text-mid)"
                          : "var(--color-outline-strong)",
                      backgroundColor:
                        selectedIndex === index
                          ? "var(--color-surface-sunken)"
                          : "var(--color-surface-strong)",
                      boxShadow:
                        selectedIndex === index
                          ? "inset 0 0 0 1px var(--color-text-mid)"
                          : undefined,
                    }}
                  >
                    {width > 2.5 ? index + 1 : ""}
                  </button>
                </React.Fragment>
              );
            })}
            <div className="absolute inset-x-0 top-[78px] border-t border-outline-strong" />
            {Array.from({ length: 5 }, (_, index) => {
              const time = start + (duration * index) / 4;
              return (
                <div
                  key={index}
                  className="absolute top-[78px] whitespace-nowrap border-l border-outline-strong pt-2 text-[10px] leading-tight text-text-dim"
                  style={{
                    left: index * 25 + "%",
                    transform:
                      index === 4
                        ? "translateX(-100%)"
                        : index === 0
                          ? undefined
                          : "translateX(-50%)",
                  }}
                >
                  <span className="block whitespace-nowrap">
                    {new Date(time).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="block">
                    {new Date(time).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-7 text-center text-[11px] text-text-dim">{t("timeline_axis")}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-outline pt-3 text-xs text-text-mid">
        <label htmlFor={selectId} className="font-medium text-text">
          {t("virtual_recording_label")}
        </label>
        <select
          id={selectId}
          value={selectedIndex}
          onChange={event => setSelected(Number(event.target.value))}
          className="rounded border border-outline-strong bg-surface px-2 py-1 text-text"
        >
          {timeline.windows.map((_, index) => (
            <option key={index} value={index}>
              {t("virtual_recording_number", { number: index + 1 })}
            </option>
          ))}
        </select>
        <span aria-live="polite">
          {fullDate(Date.parse(window.start))} – {fullDate(Date.parse(window.end))}
          {" · "}
          {formatDuration((Date.parse(window.end) - Date.parse(window.start)) / 1000)}
          {" · "}
          <strong className="font-semibold text-text">{t(cutLabel[window.cut])}</strong>
        </span>
      </div>
      <p className="mt-2 text-xs text-text-dim">{t("timeline_activity_hint")}</p>
    </figure>
  );
};

const StageWorkTable = ({ chunking }: { chunking: StageChunkingReport[] }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-outline-strong bg-surface">
        <table
          className="w-full text-left text-sm text-text"
          aria-label={t("virtual_stage_details")}
        >
          <thead className="bg-muted-surface text-xs text-text-dim">
            <tr>
              {[t("stage"), t("initial_chunks"), t("truncation_splits"), t("completed_chunks")].map(
                label => (
                  <th key={label} scope="col" className="px-3 py-2 font-medium">
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {(["lift-to-l3", "lift-to-l4"] as const).map(stage => {
              const work = chunking.find(entry => entry.stage === stage)?.chunking;
              return (
                <tr key={stage} className="border-t border-outline">
                  <th scope="row" className="px-3 py-2 font-medium">
                    {stageName(stage)}
                  </th>
                  <td className="px-3 py-2 tabular-nums">{work?.chunks ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums">{work?.splits.length ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {work ? work.chunks + work.splits.length : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-text-dim">{t("virtual_stage_work_hint")}</p>
    </div>
  );
};

export default function VirtualRecordingsContent({
  chunking,
}: {
  chunking: StageChunkingReport[];
}) {
  const { t } = useTranslation(undefined, { keyPrefix: "analysisReport" });
  if (chunking.length === 0)
    return <p className="text-sm text-text-mid">{t("no_virtual_recordings_recorded")}</p>;
  const shared = hasSharedPartition(chunking);
  const timelineEntry = chunking.find(entry => entry.chunking.timeline);
  return (
    <div className="space-y-4">
      {shared ? (
        <>
          <PartitionStats partition={chunking[0].chunking} />
          <p className="text-xs text-text-mid">
            {t("shared_partition_hint", {
              stages: chunking.map(entry => stageName(entry.stage)).join(", "),
            })}
          </p>
          {timelineEntry?.chunking.timeline ? (
            <>
              <VirtualRecordingTimeline timeline={timelineEntry.chunking.timeline} />
              {chunking.some(entry => !entry.chunking.timeline) && (
                <p className="text-xs text-text-dim">
                  {t("timeline_source", { stage: stageName(timelineEntry.stage) })}
                </p>
              )}
            </>
          ) : (
            <p className="rounded-xl border border-outline bg-surface p-4 text-sm text-text-mid">
              {t("timeline_not_recorded")}
            </p>
          )}
        </>
      ) : (
        <>
          <p className="rounded-xl border border-warning-strong/40 bg-warning/10 p-3 text-sm text-text">
            {t("partition_mismatch")}
          </p>
          {chunking.map(entry => (
            <details key={entry.stage} className="rounded-lg border border-outline-strong p-3">
              <summary className="cursor-pointer text-sm font-medium text-text">
                {stageName(entry.stage)}
              </summary>
              <div className="mt-3 space-y-3">
                <PartitionStats partition={entry.chunking} />
                {entry.chunking.timeline ? (
                  <VirtualRecordingTimeline timeline={entry.chunking.timeline} />
                ) : (
                  <p className="text-sm text-text-mid">{t("timeline_not_recorded")}</p>
                )}
              </div>
            </details>
          ))}
        </>
      )}
      <h4 className="pt-2 text-xs font-semibold uppercase tracking-wider text-text-dim">
        {t("virtual_stage_details")}
      </h4>
      <StageWorkTable chunking={chunking} />
    </div>
  );
}
